import {Box, Button, Paper, Stack, ToggleButton, ToggleButtonGroup, Typography, styled} from '@mui/material';

export const TariffsRoot = styled(Stack)(() => ({
  width: 1350,
  maxWidth: '92vw',
  margin: '0 auto',
  backgroundColor: '#FFFFFF',
}));

export const TariffsSurface = styled(Stack)(({theme}) => ({
  background: 'linear-gradient(180deg, #F5F6FA 0%, #D8E5F2 100%)',
  borderRadius: 10,
  padding: theme.spacing(6, 6, 7),
  gap: theme.spacing(3),
}));

export const Heading = styled(Stack)(() => ({
  alignItems: 'center',
  gap: 12,
}));

export const Title = styled(Typography)(() => ({
  fontWeight: 400,
  fontSize: 40,
  lineHeight: '110%',
  textAlign: 'center',
  letterSpacing: '-0.05em',
  color: '#1A1A1A',
}));

export const Subtitle = styled(Typography)(() => ({
  fontWeight: 400,
  fontSize: 18,
  lineHeight: '145%',
  textAlign: 'center',
  letterSpacing: '-0.03em',
  color: '#4F4F4F',
}));

export const PeriodToggle = styled(ToggleButtonGroup)(() => ({
  background: '#FFFFFF',
  boxShadow: '0px 10px 15px rgba(24, 59, 86, 0.2)',
  borderRadius: 27,
  padding: '8px 10px',
  gap: 4,
  height: 48,

  '& .MuiToggleButton-root': {
    border: 'none',
    borderRadius: 26,
    padding: '6px 12px',
    fontWeight: 600,
    fontSize: 14,
    color: '#4F4F4F',
    textTransform: 'none',
  },
  '& .MuiToggleButton-root.Mui-selected': {
    background: '#0D99FF',
    color: '#FFFFFF',
  },
  '& .MuiToggleButton-root.Mui-selected:hover': {
    background: '#0D99FF',
  },
}));

export const PeriodOption = styled(ToggleButton)(() => ({
  '&&&': {
    fontSize: 16,
  },
}));

export const SaveHint = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'visible',
})<{visible?: boolean}>(({visible}) => ({
  minHeight: 24,
  fontWeight: 400,
  fontSize: 16,
  lineHeight: '24px',
  color: '#4F4F4F',
  textAlign: 'center',
  visibility: visible ? 'visible' : 'hidden',
}));

export const CardsRow = styled(Stack)(() => ({
  width: '100%',
  flexDirection: 'row',
  gap: 20,
  justifyContent: 'center',
  marginTop: 40,
}));

export const PlanCard = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'highlighted',
})<{highlighted?: boolean}>(({highlighted}) => ({
  '&': {
    width: 278,
    minHeight: 375,
    padding: '32px 16px',
    borderRadius: 12,
    border: '0.5px solid #B9B9B9',
    boxShadow: '1px 1px 15px rgba(24, 59, 86, 0.2)',
    background: highlighted ? '#E2F0FE' : '#FFFFFF',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 16,
    cursor: 'pointer',
  },
}));

export const PopularBadge = styled(Box)(() => ({
  position: 'absolute',
  top: -14,
  width: 'calc(100% + 8px)',
  alignSelf: 'center',
  height: 28,
  padding: '0 16px',
  background: '#0D99FF',
  color: '#FFFFFF',
  borderTopLeftRadius: 12,
  borderTopRightRadius: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 16,
  fontWeight: 500,
  boxShadow: '0px 2px 5px rgba(24, 59, 86, 0.25)',
}));

export const PlanName = styled(Typography)(() => ({
  fontWeight: 600,
  fontSize: 28,
  textAlign: 'center',
  letterSpacing: '-0.4px',
  color: '#2A2E30',
}));

export const PlanStorage = styled(Typography)(() => ({
  fontWeight: 400,
  fontSize: 16,
  textAlign: 'center',
  color: '#7E8285',
  borderBottom: '1px solid #B9B9B9',
  width: '100%',
}));

export const PriceRow = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'baseline',
  gap: 6,
}));

export const Price = styled(Typography)(() => ({
  fontWeight: 600,
  fontSize: 30,
  lineHeight: '32px',
  color: '#000000',
}));

export const PriceUnit = styled(Typography)(() => ({
  fontWeight: 400,
  fontSize: 24,
  color: '#000000',
}));

export const FeatureList = styled(Stack)(() => ({
  gap: 12,
  alignItems: 'flex-start',
  width: '100%',
}));

export const FeatureRow = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8,
  fontSize: 14,
  color: '#1A1A1A',
}));

export const ActionButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{active?: boolean}>(({active}) => ({
  marginTop: 'auto',
  height: 40,
  padding: '8px 17px',
  borderRadius: 6,
  boxShadow: '1px 1px 6px rgba(0, 0, 0, 0.35)',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: 16,

  ...(active
    ? {
        background: '#0D99FF',
        color: '#FFFFFF',
        '&.Mui-disabled': {
          background: '#0D99FF',
          color: '#FFFFFF',
          opacity: 1,
          boxShadow: '1px 1px 6px rgba(0, 0, 0, 0.35)',
        },
      }
    : {
        background: '#FFFFFF',
        color: '#000000',
        '&:hover': {
          background: 'inherit',
        },
        '&.Mui-disabled': {
          background: '#FFFFFF',
          color: '#000000',
          opacity: 1,
          boxShadow: '1px 1px 6px rgba(0, 0, 0, 0.35)',
        },
      }),
}));

export const InfoBar = styled(Stack)(() => ({
  width: '100%',
  background: '#FFFFFF',
  border: '0.5px solid #B9B9B9',
  boxShadow: '1px 1px 15px rgba(24, 59, 86, 0.2)',
  borderRadius: 12,
  padding: '18px 24px',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-evenly',
  gap: 24,
  marginTop: 66,
}));

export const InfoItem = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: 34,
  color: '#424446',
  fontSize: 20,
}));
